import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { Button } from "@mui/material";
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useAppSelector } from "../../hooks";
import { useRouter } from "../../hooks/useRouter";
import { useViewport } from "../../hooks/useViewport";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";
import { ROUTES } from "../../routes";
import { clearRoomMessages, updateRoomMessages } from "../../store/reducers/bToCState";
import { AuthenticateState, TBToCMessage } from "../../types/types";
import { getProticoClient } from "../../utils/api";
import { notEmpty } from "../../utils/format.utils";
import Chat from "./Chat";
import ChatListItem from "./ChatListItem";
import "./index.css";
import { useWhiteboardRoom } from "../../hooks/useWhiteboardRoom";


interface MatchParams {
  toDomain: string;
}

interface Props extends RouteComponentProps<MatchParams> {}

const BtoCPage: FC<Props> = ({ match }) => {
  const { query }: { [key: string]: any } = useRouter();
  const { logined, isProticoAuthenticated, logout, proticoAuthenticate } = useWeb3Auth();
  const { toDomain } = match.params;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const { currentAddress } = useAppSelector((state) => state.auth);
  const { roomInformations } = useAppSelector((state) => state.bToCState);
  const { roomDomain } = useWhiteboardRoom();
  const { authenticateStatus } = useAppSelector((state) => state.auth);
  const { lastBToCTarget } = useAppSelector((state) => state.bToCState);

  const { width } = useViewport();

  const lastMessageList = useMemo(() => {
    let results: Array<TBToCMessage> = [];

    for (const [_, roomInformation] of Object.entries(roomInformations)) {
      let messages: Array<TBToCMessage> = [];
      for (const [id, message] of Object.entries(roomInformation.messageList)) {
        messages.push(message);
      }
      messages.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      if (messages.length > 0) results.push(messages[0]);
    }
    return results.sort(
      (a, b) =>
        b.toDomain == roomDomain ? 1 : a.toDomain == roomDomain ? -1 : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [roomInformations]);
  const { userBlockList } = useAppSelector((state) => state.profile);

  const getMessages = () => {
    setIsFetching(true);
    dispatch(
      clearRoomMessages()
    );
    getProticoClient()
      .bToCMessages.getPreview(currentAddress)
      .then((historyMessageList) => {
        const processedMessages = historyMessageList
          .map((historyMessage) => {
            try {
              return {
                ...historyMessage,
              } as unknown as TBToCMessage;
            } catch (_) {
              return undefined;
            }
          })
          .filter(notEmpty)
          .filter(
            (message) =>
              !userBlockList.includes(message.fromAddress) &&
              !userBlockList.includes(message.toDomain)
          );
        processedMessages.forEach((message) => {
          dispatch(
            updateRoomMessages({
              toDomain: message.toDomain,
              messageText: { ...message, sended: true },
            })
          );
        });
        setIsFetching(false);
      });
  };

  const renderButton = (
    textKey: string,
    btnKey: string,
    onClickHandler: any,
    extraChildren?: ReactNode
  ) => {
    return (
      <div className="d2c-login-reminder-container">
        <VerifiedUserIcon fontSize="medium"/>
        <h3>
          {t(textKey)}
        </h3>
        <Button
          className="d2c-wallet-connect-btn"
          variant="outlined"
          onClick={onClickHandler}
        >
          {t(btnKey)}
        </Button>
        {extraChildren}
      </div>
    );
  };

  const handleReConnect = (e: React.MouseEvent<HTMLElement>) => {
    // after logout, it will be redirect to b to c page
    logout(`${ROUTES.B_TO_C_PAGE}/${
      lastBToCTarget?.toDomain && lastBToCTarget.toDomain || roomDomain
    }`
  );
  };

  const handleProticoAuthenticate = () => {
    proticoAuthenticate();
  };

  useEffect(() => {
    if (isProticoAuthenticated) {
      getMessages();
    }
  }, [isProticoAuthenticated]);

  const getMenuView = useMemo(() => {
    if (isFetching)
      return (
        <div className="body-container">
          <div className="dot-falling" />
        </div>
      );
    if (lastMessageList.length === 0)
      return <ChatListItem key={`FriendItem_0`} toDomain={roomDomain} />;

    return (
      <ul>
        {lastMessageList[0].toDomain !== roomDomain && (
          <ChatListItem key={`FriendItem_init`} toDomain={roomDomain} />
        )}
        {lastMessageList.map((message, index) => {
          return (
            <ChatListItem
              key={`D2CItem_${index}`}
              message={message}
              toDomain={toDomain}
            />
          );
        })}
      </ul>
    );
  }, [isFetching, lastMessageList, toDomain]);
  if (!logined)
    return (
      <Redirect
        to={{
          pathname: ROUTES.LOGIN_PAGE,
        }}
      />
    );

  if (authenticateStatus === AuthenticateState.GUEST) {
        return renderButton("B_TO_C_Signin_Request", "PIN_Signin_Connect_Wallet", handleReConnect);
    } else if (
      authenticateStatus === AuthenticateState.WALLET &&
      isProticoAuthenticated === false
    ) {
      return renderButton(
        "PIN_Profile_Signin_Request",
        "PIN_Signin_Verify_Wallet",
        handleProticoAuthenticate
      );
    }

  // return <React.Fragment>{getview}</React.Fragment>;
  return (
    <div className="d2c-container">
      <>
        {width > 650 ? (
          <div
            className="d2c-main-menu"
            style={{
              width: "250px",
            }}
          >
            {getMenuView}
          </div>
        ) : (
          <div className="d2c-main-menu">{getMenuView}</div>
        )}
      </>
      <div className="d2c-sub-container">
        {!!toDomain ? (
          <Chat toDomain={toDomain} />
        ) : (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "row",
              margin: "20px 10px 20px 3px"
            }}
          >
              {"👈"}
              <h4 style={{ textAlign: "left", margin: "0px 0px 0px 5px" }}>{t("B_TO_C_Index_Message")}</h4>
          </div>
        )}
      </div>
    </div>
  );
};
export default BtoCPage;
